import React, { useState, useMemo } from "react";
import { Box, Typography, Divider, Chip } from "@mui/material";
import moment from "moment";
import { ManagementActivity } from "../../../../Types";
import styles from "../CustomScrollBar.module.css";

interface ManagementLogsProps {
  logs: ManagementActivity[];
  feature?: string;
}

const ManagementLogs: React.FC<ManagementLogsProps> = ({
  logs,
  feature = "",
}) => {
  const [selectedFilter, setSelectedFilter] = useState<string>("all");

  const formatActivityType = (activityType: string) => {
    return activityType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "Approved";
      case "pending":
        return "Pending";
      case "frozen":
        return "Frozen";
      case "rejected":
        return "Rejected";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
  };

  // Get unique activity types for filtering
  const activityTypes = useMemo(() => {
    const types = logs.map((log) => log.activityType);
    return ["all", ...Array.from(new Set(types))];
  }, [logs]);

  // Filter logs based on selected activity type
  const filteredLogs = useMemo(() => {
    if (selectedFilter === "all") return logs;
    return logs.filter((log) => log.activityType === selectedFilter);
  }, [logs, selectedFilter]);

  return (
    <Box
      sx={{
        padding: 2,
        width: "100%",
        minHeight: "inherit",
        display: "flex",
        flexDirection: "column",
        justifyContent: logs?.length > 0 ? "flex-start" : "center",
        alignItems: "center",
      }}
    >
      {/* Filter Tags */}
      {logs?.length > 0 && (
        <Box
          sx={{
            mb: 3,
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {activityTypes.map((activityType) => (
            <Chip
              key={activityType}
              label={
                activityType === "all"
                  ? "All"
                  : formatActivityType(activityType)
              }
              onClick={() => setSelectedFilter(activityType)}
              sx={{
                backgroundColor:
                  selectedFilter === activityType ? "#1B8368" : "#f5f5f5",
                color: selectedFilter === activityType ? "white" : "#666",
                "&:hover": {
                  backgroundColor:
                    selectedFilter === activityType ? "#1B8368" : "#e0e0e0",
                },
                cursor: "pointer",
              }}
            />
          ))}
        </Box>
      )}

      {filteredLogs?.length > 0 ? (
        filteredLogs
          .slice()
          .reverse()
          .map((item: ManagementActivity, index: number) => (
            <Box key={item._id} sx={{ marginBottom: 2, width: "100%" }}>
              <Typography
                variant="h6"
                sx={{
                  textTransform: "capitalize",
                  //   color: "#1B8368",
                  fontWeight: "bold",
                }}
              >
                {formatActivityType(item.activityType)}
                {/* -{" "} */}
                {/* {getStatusLabel(item.status)} */}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#1B8368",
                  fontWeight: "bold",
                  marginBottom: 1,
                }}
              >
                {item?.userDetails?.name ?? "Unknown"}
              </Typography>
              <Typography variant="subtitle2" sx={{}}>
                Date & Time
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#1B8368",
                  fontWeight: "bold",
                  marginBottom: 1,
                }}
              >
                {moment
                  .utc(item.createdAt)
                  .local()
                  .format("DD MMM, YYYY | hh:mm A")}
              </Typography>
              <Typography variant="subtitle2" sx={{ marginBottom: "10px" }}>
                Message
                {feature && ` (${feature})`}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  marginBottom: 2,
                  borderRadius: 2,
                }}
                className={styles["custom-scrollbar"]}
                style={{
                  maxHeight:
                    item.message && item.message.length > 180
                      ? "200px"
                      : "auto",
                  overflowY:
                    item.message && item.message.length > 180
                      ? "auto"
                      : "hidden",
                }}
              >
                {item.message || "No message provided"}
              </Typography>

              {index !== filteredLogs?.length - 1 && (
                <Divider sx={{ borderWidth: "thin", borderColor: "#e6e2e2" }} />
              )}
            </Box>
          ))
      ) : (
        <div>No Logs Found</div>
      )}
    </Box>
  );
};

export default ManagementLogs;
